import { IMove, Position, GameState } from "../types";
import { ZodiacEngine } from "../zodiacEngine";
import { PlainHeuristic } from "./PlainHeuristic";
// TacticalHeuristic removed - now using MCTS_v2 priority system

/**
 * Interface for heuristic implementations used by the AI
 */
export interface HeuristicInterface {
  /**
   * Name of the heuristic
   */
  name: string;

  /**
   * Evaluates a move from the current state
   *
   * @param engine The ZodiacEngine instance
   * @param gameState Current game state
   * @param fromPosition Position of the piece to move
   * @param move The potential move to evaluate
   * @returns Score based on the heuristic's criteria
   */
  evaluateMove(
    engine: ZodiacEngine,
    gameState: GameState,
    fromPosition: Position,
    move: IMove
  ): number;

  /**
   * Evaluates a static position (for use at MCTS leaf nodes)
   *
   * @param gameState The game state to evaluate
   * @returns A score representing the position's value from dark player's perspective
   */
  evaluatePosition(gameState: GameState): number;

  /**
   * Policy filter to exclude certain types of moves from consideration
   *
   * @param engine The ZodiacEngine instance
   * @param gameState Current game state
   * @param fromPosition Position of the piece to move
   * @param legalMoves Array of legal moves to filter
   * @returns Filtered array of legal moves that pass the policy
   */
  policyFilter(
    engine: ZodiacEngine | undefined,
    gameState: GameState,
    fromPosition: Position,
    legalMoves: IMove[]
  ): IMove[];

  /**
   * Enable or disable debug logging
   * @param debug Whether to enable debug logging
   */
  setDebugging(debug: boolean): void;
}

/**
 * Enum of available heuristics
 */
export enum HeuristicType {
  Plain = "Plain",
  Tactical = "Tactical",
}

/**
 * Factory to get heuristic by type
 */
export function getHeuristic(type: HeuristicType): HeuristicInterface {
  // Tactical heuristic removed - now using MCTS_v2 priority system
  if (type === HeuristicType.Tactical) return new PlainHeuristic(); // Fallback to Plain
  return new PlainHeuristic();
}
